import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Sparkles,
  Wand2,
  Save,
  Trash2,
  ArrowRightCircle,
  Target,
  Calendar,
  Plane,
  Heart,
  Wallet,
  Briefcase,
  TrendingUp,
  Lightbulb,
} from "lucide-react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { API } from "../utils/api";

const formatCurrency = (value, currency = "EUR") => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const DEFAULTS = {
  current_age: 35,
  retirement_age: 60,
  life_expectancy: 85,
  current_monthly_expenses: 3000,
  holidays_per_year: 2,
  cost_per_holiday: 3000,
  expected_monthly_pension: 1000,
  annual_gift_to_kids: 0,
  gift_years: 0,
  lifestyle: "moderate",
  inflation_rate_pct: 3,
  expected_return_pct: 5,
};

export default function Retirement({ user }) {
  const currency = user?.default_currency || "EUR";
  const [form, setForm] = useState(DEFAULTS);
  const [generating, setGenerating] = useState(false);
  const [current, setCurrent] = useState(null); // currently displayed plan
  const [history, setHistory] = useState([]);
  const [converting, setConverting] = useState(false);

  const yearsToRetire = useMemo(
    () => Math.max(0, (form.retirement_age || 0) - (form.current_age || 0)),
    [form.retirement_age, form.current_age]
  );

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/retirement/plans`);
      setHistory(res.data || []);
      if (!current && res.data && res.data.length > 0) {
        setCurrent(res.data[0]);
      }
    } catch (err) {
      console.error("history load failed", err);
    }
  };

  const setNum = (key) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [key]: v === "" ? "" : Number(v) }));
  };

  const generate = async () => {
    if (form.retirement_age <= form.current_age) {
      toast.error("Retirement age must be greater than current age");
      return;
    }
    try {
      setGenerating(true);
      const payload = { ...form, currency };
      const res = await axios.post(`${API}/retirement/generate`, payload);
      setCurrent(res.data);
      setHistory((h) => [res.data, ...h]);
      toast.success("Your retirement plan is ready");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to generate plan";
      toast.error(typeof msg === "string" ? msg : "Failed to generate plan");
    } finally {
      setGenerating(false);
    }
  };

  const removePlan = async (planId) => {
    try {
      await axios.delete(`${API}/retirement/plans/${planId}`);
      setHistory((h) => h.filter((p) => p.plan_id !== planId));
      if (current?.plan_id === planId) {
        setCurrent(null);
      }
      toast.success("Removed");
    } catch (err) {
      toast.error("Could not delete");
    }
  };

  const convertToFuturePlan = async () => {
    if (!current) return;
    try {
      setConverting(true);
      const res = await axios.post(
        `${API}/retirement/plans/${current.plan_id}/convert-to-future-plan`
      );
      toast.success(`Created Future Plan: ${res.data.name}`);
    } catch (err) {
      toast.error("Could not create Future Plan");
    } finally {
      setConverting(false);
    }
  };

  const ai = current?.ai;
  const c = current?.currency || currency;

  return (
    <Layout user={user} pageTitle="Retirement Goal">
      <div data-testid="retirement-page" className="space-y-6">
        <p className="text-gray-400 max-w-3xl">
          Tell us how you want to live after work and we'll size the nest egg you need.
          Our AI planner factors in inflation, lifestyle, holidays, kids, and pension
          income to recommend a target corpus and monthly savings.
        </p>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* INPUTS */}
          <Card className="bg-card border-border xl:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-green-400" />
                Your inputs
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Current age" testid="ret-current-age">
                  <Input
                    type="number"
                    value={form.current_age}
                    onChange={setNum("current_age")}
                    className="bg-secondary border-border text-white"
                    data-testid="ret-current-age-input"
                  />
                </Field>
                <Field label="Retirement age" testid="ret-retirement-age">
                  <Input
                    type="number"
                    value={form.retirement_age}
                    onChange={setNum("retirement_age")}
                    className="bg-secondary border-border text-white"
                    data-testid="ret-retirement-age-input"
                  />
                </Field>
                <Field label="Life expectancy">
                  <Input
                    type="number"
                    value={form.life_expectancy}
                    onChange={setNum("life_expectancy")}
                    className="bg-secondary border-border text-white"
                    data-testid="ret-life-expectancy-input"
                  />
                </Field>
                <Field label={`Monthly expenses (${c})`}>
                  <Input
                    type="number"
                    value={form.current_monthly_expenses}
                    onChange={setNum("current_monthly_expenses")}
                    className="bg-secondary border-border text-white"
                    data-testid="ret-expenses-input"
                  />
                </Field>
                <Field label="Holidays / year">
                  <Input
                    type="number"
                    value={form.holidays_per_year}
                    onChange={setNum("holidays_per_year")}
                    className="bg-secondary border-border text-white"
                    data-testid="ret-holidays-input"
                  />
                </Field>
                <Field label={`Avg cost / holiday (${c})`}>
                  <Input
                    type="number"
                    value={form.cost_per_holiday}
                    onChange={setNum("cost_per_holiday")}
                    className="bg-secondary border-border text-white"
                    data-testid="ret-holiday-cost-input"
                  />
                </Field>
                <Field label={`Monthly pension (${c})`}>
                  <Input
                    type="number"
                    value={form.expected_monthly_pension}
                    onChange={setNum("expected_monthly_pension")}
                    className="bg-secondary border-border text-white"
                    data-testid="ret-pension-input"
                  />
                </Field>
                <Field label={`Annual kids gift (${c})`}>
                  <Input
                    type="number"
                    value={form.annual_gift_to_kids}
                    onChange={setNum("annual_gift_to_kids")}
                    className="bg-secondary border-border text-white"
                    data-testid="ret-kids-amount-input"
                  />
                </Field>
                <Field label="Gift duration (years)">
                  <Input
                    type="number"
                    value={form.gift_years}
                    onChange={setNum("gift_years")}
                    className="bg-secondary border-border text-white"
                    data-testid="ret-kids-years-input"
                    placeholder="e.g. 10"
                  />
                </Field>
              </div>

              {form.annual_gift_to_kids > 0 && (
                <p className="text-[11px] text-gray-500 -mt-1">
                  You'll gift {formatCurrency(form.annual_gift_to_kids, c)} per year to your kids
                  for the first {form.gift_years || 0} year{form.gift_years === 1 ? "" : "s"} of retirement.
                </p>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Lifestyle">
                  <Select
                    value={form.lifestyle}
                    onValueChange={(v) => setForm((f) => ({ ...f, lifestyle: v }))}
                  >
                    <SelectTrigger
                      className="bg-secondary border-border text-white"
                      data-testid="ret-lifestyle-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="moderate" className="text-gray-300 focus:text-white focus:bg-secondary">
                        Moderate
                      </SelectItem>
                      <SelectItem value="premium" className="text-gray-300 focus:text-white focus:bg-secondary">
                        Premium
                      </SelectItem>
                      <SelectItem value="luxury" className="text-gray-300 focus:text-white focus:bg-secondary">
                        Luxury
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Inflation %">
                  <Input
                    type="number"
                    step="0.1"
                    value={form.inflation_rate_pct}
                    onChange={setNum("inflation_rate_pct")}
                    className="bg-secondary border-border text-white"
                    data-testid="ret-inflation-input"
                  />
                </Field>
                <Field label="Return % p.a.">
                  <Input
                    type="number"
                    step="0.1"
                    value={form.expected_return_pct}
                    onChange={setNum("expected_return_pct")}
                    className="bg-secondary border-border text-white"
                    data-testid="ret-return-input"
                  />
                </Field>
              </div>

              <p className="text-xs text-gray-500">
                Years until retirement: <span className="text-white font-semibold">{yearsToRetire}</span>
              </p>

              <Button
                onClick={generate}
                disabled={generating}
                className="w-full bg-green-500 hover:bg-green-400 text-black rounded-full font-semibold"
                data-testid="ret-generate-btn"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                {generating ? "Asking the planner..." : "Generate retirement plan"}
              </Button>
            </CardContent>
          </Card>

          {/* RESULTS */}
          <div className="xl:col-span-2 space-y-6">
            {!ai && !generating && (
              <Card className="bg-card border-border">
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1">No plan yet</h3>
                  <p className="text-gray-400">
                    Fill the form on the left and hit "Generate retirement plan".
                  </p>
                </CardContent>
              </Card>
            )}

            {generating && (
              <Card className="bg-card border-border">
                <CardContent className="p-10 flex flex-col items-center gap-4">
                  <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-gray-300">
                    Modelling inflation, lifestyle, holidays, pension and drawdown...
                  </p>
                </CardContent>
              </Card>
            )}

            {ai && (
              <motion.div
                key={current.plan_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Headline numbers */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Stat
                    label="Target corpus at retirement"
                    value={formatCurrency(ai.target_corpus, c)}
                    icon={<Target className="w-5 h-5 text-green-400" />}
                    accent="text-green-400"
                    testid="ret-target-corpus"
                  />
                  <Stat
                    label="Monthly savings needed"
                    value={formatCurrency(ai.monthly_savings_needed, c)}
                    icon={<Wallet className="w-5 h-5 text-amber-400" />}
                    accent="text-amber-400"
                    testid="ret-monthly-savings"
                  />
                  <Stat
                    label="Lump sum alternative today"
                    value={formatCurrency(ai.lump_sum_alternative, c)}
                    icon={<Briefcase className="w-5 h-5 text-blue-400" />}
                    accent="text-blue-400"
                    testid="ret-lump-sum"
                  />
                </div>

                {/* Summary + assumptions */}
                <Card className="bg-card border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-bold text-white">Plan summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-gray-300 leading-relaxed">{ai.summary}</p>
                    {(() => {
                      const inp = current.inputs || {};
                      const lm = ai.assumptions?.lifestyle_multiplier || 1;
                      const todayExpense =
                        (inp.current_monthly_expenses || 0) * 12 * lm;
                      const todayHoliday =
                        (inp.holidays_per_year || 0) * (inp.cost_per_holiday || 0);
                      const todayPension =
                        (inp.expected_monthly_pension || 0) * 12;
                      return (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
                          <Pill
                            icon={<TrendingUp className="w-3.5 h-3.5" />}
                            label="Annual expense"
                            todayValue={formatCurrency(todayExpense, c)}
                            futureValue={formatCurrency(
                              ai.assumptions?.annual_expense_at_retirement,
                              c
                            )}
                          />
                          <Pill
                            icon={<Briefcase className="w-3.5 h-3.5" />}
                            label="Annual pension"
                            todayValue={formatCurrency(todayPension, c)}
                            futureValue={formatCurrency(
                              ai.assumptions?.annual_pension_income,
                              c
                            )}
                          />
                          <Pill
                            icon={<Plane className="w-3.5 h-3.5" />}
                            label="Holiday spend / yr"
                            todayValue={formatCurrency(todayHoliday, c)}
                            futureValue={formatCurrency(
                              ai.assumptions?.annual_holiday_cost_at_retirement,
                              c
                            )}
                          />
                          <Pill
                            icon={<Heart className="w-3.5 h-3.5" />}
                            label="Lifestyle multiplier"
                            value={`×${ai.assumptions?.lifestyle_multiplier ?? "—"}`}
                          />
                          <Pill
                            icon={<Calendar className="w-3.5 h-3.5" />}
                            label="Real return"
                            value={`${ai.assumptions?.real_return_pct ?? "—"}%`}
                          />
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                {/* Recommendations */}
                {Array.isArray(ai.recommendations) && ai.recommendations.length > 0 && (
                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-yellow-400" />
                        Personalized tips
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {ai.recommendations.map((tip, i) => (
                          <li
                            key={i}
                            className="text-gray-300 text-sm leading-relaxed flex gap-2"
                            data-testid={`ret-tip-${i}`}
                          >
                            <span className="text-green-400">•</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Drawdown table */}
                {Array.isArray(ai.yearly_drawdown) && ai.yearly_drawdown.length > 0 && (
                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-bold text-white">
                        Year-by-year drawdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto max-h-96">
                        <table className="w-full text-sm" data-testid="ret-drawdown-table">
                          <thead className="sticky top-0 bg-card">
                            <tr className="border-b border-border text-gray-400">
                              <th className="py-2 text-left font-medium">Age</th>
                              <th className="py-2 text-right font-medium">Starting</th>
                              <th className="py-2 text-right font-medium">Expense</th>
                              <th className="py-2 text-right font-medium">Pension</th>
                              <th className="py-2 text-right font-medium">Kids</th>
                              <th className="py-2 text-right font-medium">Interest</th>
                              <th className="py-2 text-right font-medium">Ending</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ai.yearly_drawdown.map((row, idx) => (
                              <tr key={idx} className="border-b border-border/50">
                                <td className="py-2 text-white font-medium">{row.age}</td>
                                <td className="py-2 text-right text-gray-300 number-display">
                                  {formatCurrency(row.starting_balance, c)}
                                </td>
                                <td className="py-2 text-right text-red-400 number-display">
                                  -{formatCurrency(row.expense, c)}
                                </td>
                                <td className="py-2 text-right text-green-400 number-display">
                                  +{formatCurrency(row.pension, c)}
                                </td>
                                <td className="py-2 text-right text-amber-400 number-display">
                                  {row.kids_gift ? `-${formatCurrency(row.kids_gift, c)}` : "-"}
                                </td>
                                <td className="py-2 text-right text-blue-400 number-display">
                                  +{formatCurrency(row.interest, c)}
                                </td>
                                <td className="py-2 text-right font-bold text-white number-display">
                                  {formatCurrency(row.ending_balance, c)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="flex flex-col sm:flex-row justify-end gap-2">
                  <Button
                    onClick={convertToFuturePlan}
                    disabled={converting}
                    className="rounded-full bg-green-500 hover:bg-green-400 text-black font-semibold"
                    data-testid="ret-convert-btn"
                  >
                    <ArrowRightCircle className="w-4 h-4 mr-2" />
                    {converting ? "Creating..." : "Convert to Future Plan"}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* History */}
            {history.length > 0 && (
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
                    <Save className="w-4 h-4 text-green-400" />
                    Saved plans
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {history.map((p) => {
                      const isActive = current?.plan_id === p.plan_id;
                      return (
                        <div
                          key={p.plan_id}
                          className={`flex items-center justify-between gap-2 p-3 rounded-xl border ${
                            isActive
                              ? "border-green-500/40 bg-green-500/5"
                              : "border-border bg-secondary/40"
                          }`}
                          data-testid={`ret-history-row-${p.plan_id}`}
                        >
                          <button
                            onClick={() => setCurrent(p)}
                            className="text-left flex-1 min-w-0"
                          >
                            <p className="text-sm text-white truncate">
                              Retire at {p.inputs?.retirement_age} •{" "}
                              <span className="text-green-400 number-display">
                                {formatCurrency(p.ai?.target_corpus, p.currency)}
                              </span>{" "}
                              target
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {p.inputs?.lifestyle} • {formatCurrency(p.ai?.monthly_savings_needed, p.currency)}/mo •{" "}
                              {new Date(p.created_at).toLocaleString()}
                            </p>
                          </button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removePlan(p.plan_id)}
                            className="text-gray-500 hover:text-red-400"
                            data-testid={`ret-history-delete-${p.plan_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-gray-300 text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Stat({ label, value, icon, accent, testid }) {
  return (
    <Card className="bg-card border-border" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wider">
          {icon}
          <span>{label}</span>
        </div>
        <p className={`text-2xl font-bold number-display mt-2 ${accent}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function Pill({ icon, label, value, todayValue, futureValue }) {
  return (
    <div className="p-3 bg-secondary rounded-xl">
      <div className="flex items-center gap-2 text-[10px] text-gray-500 uppercase tracking-wider">
        {icon}
        <span>{label}</span>
      </div>
      {value !== undefined ? (
        <p className="text-sm font-semibold text-white number-display mt-1">{value}</p>
      ) : (
        <div className="mt-1 space-y-0.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[10px] text-gray-500">Today</span>
            <span className="text-xs text-gray-400 number-display">{todayValue}</span>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[10px] text-green-400">At retirement</span>
            <span className="text-sm font-semibold text-white number-display">{futureValue}</span>
          </div>
        </div>
      )}
    </div>
  );
}
